import React, { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import styles from './PaymentPlanChoosePaymentMethodPageData.module.scss';
import PaymentMethodForm from './components/PaymentMethodForm/PaymentMethodForm';
import StepIndicator from 'components/StepIndicator/StepIndicator';
import RepaymentList from 'components/RepaymentList/RepaymentList';
import { useAppSelector } from 'app/hooks';
import { selectCurrentMarket } from 'features/core/selectors';
import { Market } from 'common/consts/markets';
import { getGTMVariables, getGTMVariablesPageView } from 'common/helpers/Gtm';
import TagManager from 'react-gtm-module';

interface Props {
  PageData?: any;
  Model?: any;
  LayoutModel?: any;
}

const PaymentPlanChoosePaymentMethodPageData = (props: Props) => {
  const { Model, PageData, LayoutModel } = props;
  const currentMarket = useAppSelector(selectCurrentMarket);
  const [eventTracked, setEventTracked] = useState<boolean>(false);
  const [settlementPlanSetupEventTracked, setSettlementPlanSetupEventTracked] =
    useState<boolean>(false);

  useEffect(() => {
    if (eventTracked == false) {
      var tagManagerArgs: any;
      tagManagerArgs = {
        dataLayer: {
          event: 'checkout_step',
          checkout_name: 'Payment Plan Creation',
          checkout_step_number: '2',
          is_secured_user: LayoutModel?.IsLoggedInUserSecured,
          ...(LayoutModel?.IsLoggedInUserSecured && {
            secured_case_status: LayoutModel?.SecuredCaseStatus,
          }),
        },
      };
      TagManager.dataLayer(tagManagerArgs);
      setEventTracked(true);
    }
  }, [eventTracked]);

  useEffect(() => {
    if (LayoutModel != undefined) {
      var userid =
        LayoutModel?.Profiles.length > 0
          ? LayoutModel?.Profiles[0].DebtReference
          : '';
      var isLoggedIn = LayoutModel?.HasProfile;
      var lang = PageData?.Language;
      var env = LayoutModel?.Environment;
      var reg = LayoutModel?.CountryCode;
      getGTMVariables(
        userid,
        isLoggedIn,
        lang,
        reg,
        env,
        LayoutModel?.IsLoggedInUserSecured,
        LayoutModel?.SecuredCaseStatus,
      );
      getGTMVariablesPageView(
        window.document.title,
        window.location.href,
        window.location.pathname,
        userid,
        isLoggedIn,
        lang,
        reg,
        env,
      );
    }
  }, [LayoutModel]);
  return (
    <div className={styles.wrapper_main}>
      {!Model.HasProposedPaymentPlan && (
        <StepIndicator
          ActiveStep={Model?.ActiveWizardStep?.ActiveStep}
          TotalSteps={3}
          StepText={PageData?.StepText}
          OfText={PageData?.OfText}
        />
      )}
      <div className={styles.wrapper}>
        <div className="sectionGutterV Section--secondaryBg u-paddingTz">
          <div className={styles.container}>
            <main className="CreateRepaymentPlan u-paddingTz">
              <section className="CreateRepaymentPlan__section">
                {!Model.HasProposedPaymentPlan ? (
                  <>
                    <h1 className="CreateRepaymentPlan__heading u-marginTz">
                      <>{PageData.PaymentPlanHeader}</>
                    </h1>
                  </>
                ) : (
                  <h1 className="CreateRepaymentPlan__heading u-marginTz">
                    <>{PageData.ProposedPaymentPlanHeader}</>
                  </h1>
                )}

                {Model.ManualPaymentMethods != null &&
                  Model.ManualPaymentMethods.Count > 0 &&
                  Model.PageData.IsInformationVisible && (
                    <div className="information">
                      <div className="information-header">
                        {PageData?.InformationText}
                      </div>
                      <div className="information-description Text--small Grid-spacingB">
                        <div className="manual-payment-information">
                          {PageData?.ManualPaymentInformation}
                        </div>
                        <div className="automated-payment-information">
                          {PageData?.AutomatedPaymentInformation}
                        </div>
                      </div>
                    </div>
                  )}
              </section>

              {Model.HasProposedPaymentPlan ? (
                Model.DisplayProposedPlanBanner ? (
                  <section className="CreateRepaymentPlan__section">
                    <div className="PersonalisedPaymentPlanCard">
                      <div className="PersonalisedPaymentPlanCard__header">
                        <div className="PersonalisedPaymentPlanCard__column">
                          <span className="PersonalisedPaymentPlanCard__label">
                            {PageData?.ProposedPlanMonthlyPaymentLabel}
                          </span>
                          <span className="PersonalisedPaymentPlanCard__value">
                            {
                              Model?.FormattedCurrencyProposedPlanInstallmentAmount
                            }
                          </span>
                        </div>
                        <div className="PersonalisedPaymentPlanCard__column">
                          <span className="PersonalisedPaymentPlanCard__label">
                            {PageData?.ProposedPlanNoOfInstallmentsLabel}
                          </span>
                          <span className="PersonalisedPaymentPlanCard__value">
                            {Model?.ProposedPlanInstallmentCount}
                          </span>
                        </div>
                      </div>
                      <div className="PersonalisedPaymentPlanCard__description">
                        {PageData?.ProposedPlanFinalPaymentText}
                      </div>
                    </div>
                  </section>
                ) : (
                  !(
                    currentMarket === Market.NL ||
                    currentMarket === Market.BE ||
                    currentMarket === Market.PL ||
                    (currentMarket === Market.DE && !Model?.ForOffer)
                  ) && (
                    <RepaymentList
                      Heading={PageData?.RepaymentPlanDetailsLabel}
                      Monthlylabel={PageData?.NumberOfInstallmentsLabel}
                      Mothlyamount={Model?.NumberOfInstallments}
                      Currencysymbol={Model?.FormattedCurrencySymbol}
                      Finallabel={PageData?.FinalPaymentLabel}
                      FinalAmount={Model?.FinalToBePaid}
                      Totaltext={PageData?.TotalToBePaidLabel}
                      Totalamount={Model?.CurrentBalance}
                      DisposableIncomeLimit={Model?.DisposableIncomeLimit}
                    />
                  )
                )
              ) : (
                !(
                  currentMarket === Market.NL ||
                  currentMarket === Market.BE ||
                  currentMarket === Market.PL ||
                  (currentMarket === Market.DE && !Model?.ForOffer)
                ) && (
                  <section>
                    <RepaymentList
                      Heading={PageData?.RepaymentPlanDetailsLabel}
                      Monthlylabel={PageData?.NumberOfInstallmentsLabel}
                      Mothlyamount={Model?.NumberOfInstallments}
                      Currencysymbol={Model?.FormattedCurrencySymbol}
                      Finallabel={PageData?.FinalPaymentLabel}
                      FinalAmount={Model?.FinalToBePaid}
                      Totaltext={PageData?.TotalToBePaidLabel}
                      Totalamount={
                        Model?.DiscountedDebtBalanceValue > 0
                          ? Model?.FormattedCurrencyOfferCurrentBalance
                          : Model?.CurrentBalance
                      }
                      BalanceLabel={PageData?.PaymentPlanItemDebtBalanceLabel}
                      BalanceAmount={Model?.CurrentBalance}
                      DiscountBalancelabel={
                        PageData?.PaymentPlanItemDiscountAmountLabel
                      }
                      DiscountBalanceAmount={
                        Model?.FormattedCurrencyOfferDiscountAmount
                      }
                      DiscountedDebtBalanceRaw={Model?.DiscountAmount}
                    />
                  </section>
                )
              )}
              <PaymentMethodForm {...props} />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPlanChoosePaymentMethodPageData;
